import React from 'react';
import CircularProgress from "@material-ui/core/CircularProgress";
import Editor from "../../components/Editor";
import { GATEWAY_URL } from '../../env';
import Request from "../../components/Request";
import Localize from "../../components/Localize/Localize";
import LocalizeKeys from "../../components/Localize/LocalizeKeys";
import TriggerActionService from "../../components/TriggerActionService";
import ActionTriggerConstants from "../../components/ActionTriggerConstants";
import { setupTokens } from "../Game/utils/UrlParams";
import NavBar from "../NavBar/NavBar";
import { DataPage, DataPageModal } from "../../components/DataPage";
import CurrencyAmount from "../../components/CurrencyAmount";

const moment = require("moment");

// The backend pages the transactions (see AuthService/GetUserTransactionsByToken). The in-game
// balance popup pages them too, but this web is the "see all" destination, so it walks every page
// until there is nothing left. MAX_PAGES is just a safety net against a runaway loop.
const PAGE_SIZE = 100;
const MAX_PAGES = 200;

// Money currency types (see Currency.CurrencyType on the backend); the reason key is suffixed with
// the currency for these, the same way the client does it.
const MONEY_CURRENCIES = ["RealMoney", "BonusMoney"];

class Transactions extends React.Component {

  state = {
    loading: false,
    error: false,
    contents: null,
    selectedViewContent: null, // Holds the transaction shown in the detail modal
  };

  columns = require('./table.js').columns(this);

  async componentDidMount() {
    // Fire-and-forget async trigger action
    TriggerActionService.sendTriggerAction(ActionTriggerConstants.LANDING_OPEN, "", {
        sourceUrl: "Transactions"
    }).catch((e) => {
        console.warn("[-] TriggerActionService failed", e);
    });

    // The client opens this page with the session in the URL (see UrlBuilder.GetUserTransactionsUrl).
    // setupTokens stores it in cookies and strips it from the address bar before the first request.
    await setupTokens();

    await this.getContents();
  }

  async getContents() {
    try {
      this.setState({
        loading: true,
        error: false,
      });

      const transactions = await this.getAllTransactions();

      const output = transactions.map((x) => ({
        ...x,
        date: x.transactionDate,
        event: this.translateReason(x),
        amount: <CurrencyAmount amount={x.currencyAmount} currencyType={x.currencyType} />,
      }));

      this.setState({
        loading: false,
        contents: output,
      });

    } catch (error) {
      console.error("Error fetching transactions:", error);
      this.setState({
        error: true,
        loading: false,
      });
    }
  }

  // Walks every page of GetUserTransactionsByToken and returns the whole history.
  async getAllTransactions() {
    const transactions = [];

    for (let page = 0; page < MAX_PAGES; page++) {
      const response = await Request.GET(
          `${GATEWAY_URL}/AuthService/GetUserTransactionsByToken?page=${page}&pageSize=${PAGE_SIZE}`
      );

      // axios is configured to resolve any status < 500, so an expired/invalid token comes back
      // here as a body without userTransactions instead of as a rejection. Treat it as an error
      // rather than as "no movements".
      if (!response || !Array.isArray(response.userTransactions)) {
        throw new Error(`Unexpected GetUserTransactionsByToken response: ${JSON.stringify(response)}`);
      }

      const pageTransactions = response.userTransactions;
      transactions.push(...pageTransactions);

      // hasMore comes from the backend, but recompute it from totalCount as well so an empty or
      // short page always stops the loop.
      const hasMore = response.hasMore && pageTransactions.length > 0;
      if (!hasMore || transactions.length >= (response.totalCount || 0)) break;
    }

    return transactions;
  }

  // Mirrors BalanceElementView.GetReasonKey on the client: the in-game key is tried first so the
  // wording matches the balance popup, then the landing-specific key, then the raw reason type.
  translateReason(transaction) {
    const reasonType = transaction.reasonType || "";
    const currencyType = transaction.currencyType || "";

    let clientKey = `KEY_${reasonType}`;
    if (MONEY_CURRENCIES.includes(currencyType)) {
      clientKey += `.${currencyType}`;
    }

    const landingKey = `LANDING_TRANSACTION_REASON_${reasonType.toUpperCase()}`;

    return Localize.translate([clientKey, landingKey], { defaultValue: this.prettifyReason(reasonType) });
  }

  prettifyReason(reasonType) {
    if (!reasonType) return "--";
    const spaced = reasonType.replace(/[_.]/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  handleView = (row) => {
    this.setState({ selectedViewContent: row });
  };

  handleBack = () => {
    this.setState({ selectedViewContent: null });
  };

  // Mirrors BalanceElementView.OnInfoBtnClick on the client.
  renderTransactionDetails = (content) => {
    const isMatch = (content.reasonType || "").includes("game") || (content.reasonType || "").includes("match");

    return (
        <div>
          <p>{Localize.translate(LocalizeKeys.TRANSACTION_DATE)}: {new moment(content.date).format("YYYY-MM-DD HH:mm:ss")}</p>
          <p>{Localize.translate(LocalizeKeys.TRANSACTION_EVENT)}: {content.event}</p>
          <p>{Localize.translate(LocalizeKeys.TRANSACTION_AMOUNT)}: {content.amount}</p>
          <p>{Localize.translate(LocalizeKeys.TRANSACTION_ID)}: {content.transactionId}</p>
          {isMatch && <p>{Localize.translate(LocalizeKeys.TRANSACTION_MATCH_ID)}: {content.reasonId}</p>}
        </div>
    );
  };

  render() {
    const { loading, error, contents, selectedViewContent } = this.state;

    return (
        <React.Fragment>
          <NavBar />
          <DataPage>
            {loading && <CircularProgress style={{ textAlign: "center", marginBottom: "5px" }} />}
            {error && <p>{Localize.translate(LocalizeKeys.TRANSACTIONS_ERROR)}</p>}
            {!loading && !error && contents && (
                <div>
                  <h2 style={{ textAlign: "center", marginBottom: "5px" }}>{Localize.translate(LocalizeKeys.TRANSACTIONS_TITLE)}</h2>
                  {contents.length > 0 ? (
                      <Editor
                          data={contents}
                          columns={this.columns}
                          isEditable={false}
                          isDeletable={false}
                          isViewable={true}
                          onView={this.handleView}
                      />
                  ) : (
                      <p style={{ textAlign: "center" }}>{Localize.translate(LocalizeKeys.TRANSACTIONS_NOT_FOUND)}</p>
                  )}
                </div>
            )}
          </DataPage>
          {selectedViewContent && (
              <DataPageModal title={Localize.translate(LocalizeKeys.MORE_INFO)} onClose={this.handleBack}>
                {this.renderTransactionDetails(selectedViewContent)}
              </DataPageModal>
          )}
        </React.Fragment>
    );
  }
}

export default Transactions;
